import { useCallback, useMemo, useState } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../hooks/useToast';
import { apiSend } from '../lib/api';
import { RequestAccessModal } from '../components/RequestAccessModal';
import { HomeVariantRenderer, type BlockContext } from '../home/HomeVariantRenderer';
import { CustomLayoutDrawer } from '../home/CustomLayoutDrawer';
import { useHomeVariant, useCustomizeListener } from '../home/useHomeVariant';
import type {
  AnnouncementRecord,
  AppRecord,
  MeResponse,
} from '../lib/types';

type HomeView = 'all' | 'by-category';
const HOMEVIEW_STORAGE_KEY = 'onedesk.homeView';

const readStoredHomeView = (): HomeView => {
  try {
    const v = localStorage.getItem(HOMEVIEW_STORAGE_KEY);
    if (v === 'all' || v === 'by-category') return v;
  } catch {
    // ignore
  }
  return 'all';
};

export const Home = () => {
  const { getToken, previewUserId } = useAuth();
  const { toast } = useToast();
  const me = useApi<MeResponse>('/api/me');
  const apps = useApi<AppRecord[]>('/api/apps');
  const announcements = useApi<AnnouncementRecord[]>('/api/announcements/active');

  const prefs = (me.data?.preferences as {
    homeVariant?: 'cozy' | 'focused' | 'classic' | 'custom';
    customLayout?: Array<{ id: string; visible: boolean; size?: 'S' | 'M' | 'L' }>;
  } | null) ?? null;

  const { variant, layout, setLayout } = useHomeVariant(
    prefs as Parameters<typeof useHomeVariant>[0],
  );

  const [busyId, setBusyId] = useState<string | null>(null);
  const [requestApp, setRequestApp] = useState<AppRecord | null>(null);
  const [homeView, setHomeView] = useState<HomeView>(readStoredHomeView);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // Open the customize drawer when the header switcher requests it.
  useCustomizeListener(useCallback(() => setCustomizeOpen(true), []));

  const readOnly = previewUserId !== null;

  const togglePin = useCallback(
    async (app: AppRecord) => {
      const wasPinned = me.data?.pins.some((p) => p.appId === app.id) ?? false;
      setBusyId(app.id);

      me.mutate((prev) => {
        if (!prev) return prev;
        if (wasPinned) {
          return { ...prev, pins: prev.pins.filter((p) => p.appId !== app.id) };
        }
        return {
          ...prev,
          pins: [...prev.pins, { appId: app.id, order: prev.pins.length }],
        };
      });

      try {
        const token = await getToken();
        if (wasPinned) {
          await apiSend('DELETE', `/api/pins/${app.id}`, undefined, token);
        } else {
          await apiSend('POST', '/api/pins', { appId: app.id }, token);
        }
        me.refetch();
      } catch (err) {
        me.mutate((prev) => {
          if (!prev) return prev;
          if (wasPinned) {
            return {
              ...prev,
              pins: [...prev.pins, { appId: app.id, order: prev.pins.length }],
            };
          }
          return { ...prev, pins: prev.pins.filter((p) => p.appId !== app.id) };
        });
        toast({
          kind: 'error',
          message: `Couldn't ${wasPinned ? 'unpin' : 'pin'} ${app.name}. Try again.`,
        });
        // eslint-disable-next-line no-console
        console.error('pin toggle failed', err);
      } finally {
        setBusyId(null);
      }
    },
    [me, getToken, toast],
  );

  const pinnedApps = useMemo(() => {
    if (!me.data || !apps.data) return [];
    const byId = new Map(apps.data.map((a) => [a.id, a]));
    return me.data.pins
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((p) => byId.get(p.appId))
      .filter((a): a is AppRecord => a !== undefined);
  }, [me.data, apps.data]);

  const pinnedSet = useMemo(() => new Set(pinnedApps.map((a) => a.id)), [pinnedApps]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      if (!me.data) return;

      const ids = pinnedApps.map((a) => a.id);
      const oldIdx = ids.indexOf(active.id as string);
      const newIdx = ids.indexOf(over.id as string);
      if (oldIdx < 0 || newIdx < 0) return;
      const newIds = arrayMove(ids, oldIdx, newIdx);

      me.mutate((prev) => {
        if (!prev) return prev;
        return { ...prev, pins: newIds.map((appId, i) => ({ appId, order: i })) };
      });

      (async () => {
        try {
          const token = await getToken();
          await apiSend('PUT', '/api/pins/order', { ids: newIds }, token);
          me.refetch();
        } catch {
          me.refetch();
          toast({ kind: 'error', message: "Couldn't reorder pins. Try again." });
        }
      })();
    },
    [me, pinnedApps, getToken, toast],
  );

  const appsByCategory = useMemo(() => {
    const list = apps.data ?? [];
    const groups = new Map<string, AppRecord[]>();
    for (const app of list) {
      const arr = groups.get(app.category) ?? [];
      arr.push(app);
      groups.set(app.category, arr);
    }
    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([category, catApps]) => ({ category, apps: catApps }));
  }, [apps.data]);

  const ctx: BlockContext = {
    me: { data: me.data, loading: me.loading },
    apps: { data: apps.data, loading: apps.loading, error: apps.error },
    announcements: { data: announcements.data, loading: announcements.loading },
    pinnedApps,
    pinnedSet,
    appsByCategory,
    busyId,
    readOnly,
    togglePin,
    onDragEnd,
    setRequestApp,
    homeView,
    setHomeView,
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <RequestAccessModal app={requestApp} onClose={() => setRequestApp(null)} />
      <CustomLayoutDrawer
        open={customizeOpen}
        layout={layout}
        onClose={() => setCustomizeOpen(false)}
        onSave={(next) => void setLayout(next)}
      />

      <HomeVariantRenderer variant={variant} customLayout={layout} ctx={ctx} />

      {variant === 'custom' && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setCustomizeOpen(true)}
            className="rounded-md border border-line bg-bg-primary px-3 py-1.5 text-xs font-medium text-text-secondary hover:border-brand-navy/40 hover:text-text-primary"
          >
            ✏️ Customize layout
          </button>
        </div>
      )}
    </div>
  );
};
